import React, { useEffect, useMemo, useState } from 'react';
import { fetchHouseholds, Household } from '../../api/householdsApi';
import { fetchListeners, Listener } from '../../api/listenersApi';
import { ApiError, toApiError } from '../../api/http';
import {
  cancelVoiceInvitation,
  createManualVoiceInvitationLink,
  createVoiceInvitation,
  fetchVoiceInvitations,
  isVoiceInvitationCaptureUrl,
  replaceLegacyVoiceInvitation,
  resendVoiceInvitation,
  VoiceInvitationStatus,
} from '../../api/voiceInvitationsApi';
import { ArrowLeftIcon } from '../../components/icons';
import { FormField, Panel, PillButton, StateMessage } from '../../components/ui';
import './Voice.css';

function invitationStateLabel(state: string) {
  const labels: Record<string, string> = {
    draft: 'Link not shared',
    pending: 'Waiting for a recording',
    accepted: 'Accepted',
    consented: 'Consent complete',
    capturing: 'Recording in progress',
    completed: 'Recording complete',
    expired: 'Expired',
    revoked: 'Revoked',
  };
  return labels[state] || 'Status needs attention';
}

function invitationStatusTone(invitation: VoiceInvitationStatus) {
  if (invitation.state === 'revoked') return 'revoked';
  if (invitation.requires_updated_consent_form) return 'required';
  return invitation.consent_status;
}

function invitationStatusLabel(invitation: VoiceInvitationStatus) {
  if (invitation.state === 'revoked') return 'Invitation revoked';
  if (invitation.requires_updated_consent_form) return 'Updated consent form needed';
  if (invitation.consent_status === 'active') return 'Consent active';
  if (invitation.consent_status === 'revoked') return 'Consent revoked';
  return 'Consent required';
}

function deliveryLabel(status: string) {
  const labels: Record<string, string> = {
    disabled: 'Share link only',
    queued: 'Email queued',
    submitted: 'Email submitted',
    delivered: 'Email delivered',
    failed: 'Email needs attention',
  };
  return labels[status] || 'Delivery pending';
}

function hasEmailDelivery(status: string) {
  return ['queued', 'submitted', 'delivered', 'failed'].includes(status);
}

function captureStageLabel(stage: VoiceInvitationStatus['capture_stage']) {
  const labels: Record<VoiceInvitationStatus['capture_stage'], string> = {
    details: 'Waiting for details',
    consent: 'Waiting for consent',
    voice_samples: 'Recording voice samples',
    listener_clips: 'Recording listener messages',
    complete: 'Recordings saved',
    building: 'Making voice',
    ready: 'Voice ready',
    blocked: 'Paused',
  };
  return labels[stage];
}

function buildStatusLabel(status: VoiceInvitationStatus['build_status']) {
  const labels: Record<VoiceInvitationStatus['build_status'], string> = {
    pending: 'Waiting to make the voice',
    queue_pending: 'Voice waiting to start',
    building: 'Making the voice',
    ready: 'Voice ready',
    blocked: 'Voice preparation paused',
    failed: 'Voice needs attention',
  };
  return labels[status];
}

function formatExpiry(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Expiry unavailable';
  return `Expires ${new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(date)}`;
}

const VoiceInvitations: React.FC = () => {
  const [households, setHouseholds] = useState<Household[]>([]);
  const [listeners, setListeners] = useState<Listener[]>([]);
  const [selectedHouseholdId, setSelectedHouseholdId] = useState('');
  const [invitations, setInvitations] = useState<VoiceInvitationStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState<ReturnType<typeof toApiError> | null>(null);
  const [listError, setListError] = useState<ReturnType<typeof toApiError> | null>(null);
  const [listRetryNonce, setListRetryNonce] = useState(0);
  const [retryNonce, setRetryNonce] = useState(0);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteListenerIds, setInviteListenerIds] = useState<Set<string>>(new Set());
  const [inviteUrl, setInviteUrl] = useState('');
  const [inviteOutcome, setInviteOutcome] = useState('');
  const [inviteError, setInviteError] = useState<ReturnType<typeof toApiError> | null>(null);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    Promise.all([fetchHouseholds(), fetchListeners()])
      .then(([householdRows, listenerRows]) => {
        if (!active) return;
        setHouseholds(householdRows);
        setListeners(listenerRows);
        setSelectedHouseholdId((current) => current || householdRows[0]?.household_id || listenerRows[0]?.household_id || '');
      })
      .catch((requestError) => {
        if (active) setError(toApiError(requestError));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [retryNonce]);

  const householdIds = useMemo(() => {
    const ids = new Set(households.map((household) => household.household_id));
    listeners.forEach((listener) => ids.add(listener.household_id));
    return Array.from(ids);
  }, [households, listeners]);
  const householdListeners = listeners.filter((listener) => listener.household_id === selectedHouseholdId && listener.state === 'active');

  useEffect(() => {
    if (!selectedHouseholdId || loading) return undefined;
    let active = true;
    setListLoading(true);
    setListError(null);
    fetchVoiceInvitations(selectedHouseholdId)
      .then((rows) => { if (active) setInvitations(rows); })
      .catch((requestError) => { if (active) setListError(toApiError(requestError)); })
      .finally(() => { if (active) setListLoading(false); });
    return () => { active = false; };
  }, [loading, selectedHouseholdId, listRetryNonce]);

  const changeHousehold = (householdId: string) => {
    setSelectedHouseholdId(householdId);
    setInviteListenerIds(new Set());
    setInviteUrl('');
    setInviteOutcome('');
    setInviteError(null);
  };

  const handleInvite = async () => {
    if (!selectedHouseholdId || !inviteEmail.trim() || !inviteListenerIds.size) return;
    setCreatingInvite(true);
    setInviteError(null);
    setInviteUrl('');
    setInviteOutcome('');
    try {
      const result = await createVoiceInvitation(inviteEmail.trim(), selectedHouseholdId, Array.from(inviteListenerIds));
      if (result.capture_url !== undefined && !isVoiceInvitationCaptureUrl(result.capture_url)) {
        throw new ApiError('service_unavailable');
      }
      if (result.capture_url === undefined && !hasEmailDelivery(result.delivery_status)) {
        throw new ApiError('service_unavailable');
      }
      if (result.capture_url) {
        setInviteUrl(result.capture_url);
        setInviteOutcome('Invitation ready. Share this private link once.');
      } else {
        setInviteOutcome('Invitation email queued. Delivery status will update below.');
      }
      setInviteEmail('');
      setInviteListenerIds(new Set());
      setListRetryNonce((nonce) => nonce + 1);
    } catch (requestError) {
      setInviteError(toApiError(requestError));
    } finally {
      setCreatingInvite(false);
    }
  };

  const resend = async (invitation: VoiceInvitationStatus) => {
    setBusyId(invitation.invitation_id);
    try {
      const result = await resendVoiceInvitation(invitation.invitation_id);
      if (result.capture_url !== undefined && !isVoiceInvitationCaptureUrl(result.capture_url)) {
        throw new ApiError('service_unavailable');
      }
      if (result.capture_url === undefined && !hasEmailDelivery(result.delivery_status)) {
        throw new ApiError('service_unavailable');
      }
      setInvitations((rows) => rows.map((row) => row.invitation_id === invitation.invitation_id ? {
        ...row,
        state: result.state,
        delivery_status: result.delivery_status,
        expires_at: result.expires_at,
        is_expired: false,
        can_resend: true,
        can_revoke: row.can_revoke,
      } : row));
      if (result.capture_url) {
        setInviteUrl(result.capture_url);
        setInviteOutcome('A fresh invitation link is ready.');
        setCopyFeedback('');
      } else {
        setInviteUrl('');
        setInviteOutcome('A fresh invitation email is queued. The older link no longer works.');
        setCopyFeedback('');
      }
    } catch (requestError) {
      setListError(toApiError(requestError));
    } finally {
      setBusyId(null);
    }
  };

  const createManualLink = async (invitation: VoiceInvitationStatus) => {
    setBusyId(invitation.invitation_id);
    setListError(null);
    try {
      const result = await createManualVoiceInvitationLink(invitation.invitation_id);
      if (!isVoiceInvitationCaptureUrl(result.capture_url)) {
        throw new ApiError('service_unavailable');
      }
      setInvitations((rows) => rows.map((row) => row.invitation_id === invitation.invitation_id ? {
        ...row,
        state: result.state,
        delivery_status: result.delivery_status,
        expires_at: result.expires_at,
        is_expired: false,
      } : row));
      setInviteUrl(result.capture_url);
      setInviteOutcome('A manual recovery link is ready. Any older link no longer works.');
      setCopyFeedback('');
    } catch (requestError) {
      setListError(toApiError(requestError));
    } finally {
      setBusyId(null);
    }
  };

  const createUpdatedInvitation = async (invitation: VoiceInvitationStatus) => {
    if (!selectedHouseholdId) return;
    setBusyId(invitation.invitation_id);
    setListError(null);
    try {
      const result = await replaceLegacyVoiceInvitation(invitation.invitation_id);
      if (result.capture_url !== undefined && !isVoiceInvitationCaptureUrl(result.capture_url)) {
        throw new ApiError('service_unavailable');
      }
      if (result.capture_url === undefined && !hasEmailDelivery(result.delivery_status)) {
        throw new ApiError('service_unavailable');
      }
      if (result.capture_url) {
        setInviteUrl(result.capture_url);
        setInviteOutcome('An updated invitation is ready. The previous link no longer works.');
        setCopyFeedback('');
      } else {
        setInviteUrl('');
        setInviteOutcome('An updated invitation email is queued. The previous link no longer works.');
        setCopyFeedback('');
      }
      try {
        setInvitations(await fetchVoiceInvitations(selectedHouseholdId));
      } catch (refreshError) {
        setListError(toApiError(refreshError));
      }
    } catch (requestError) {
      setListError(toApiError(requestError));
    } finally {
      setBusyId(null);
    }
  };

  const inviteAgain = async (invitation: VoiceInvitationStatus) => {
    if (
      !selectedHouseholdId
      || !invitation.recipient_email
      || invitation.listeners.length === 0
    ) {
      setListError(toApiError(new ApiError('service_unavailable')));
      return;
    }
    setBusyId(invitation.invitation_id);
    setListError(null);
    setInviteUrl('');
    setCopyFeedback('');
    try {
      const result = await createVoiceInvitation(
        invitation.recipient_email,
        selectedHouseholdId,
        invitation.listeners.map((listener) => listener.listener_id),
      );
      if (result.capture_url !== undefined && !isVoiceInvitationCaptureUrl(result.capture_url)) {
        throw new ApiError('service_unavailable');
      }
      if (result.capture_url === undefined && !hasEmailDelivery(result.delivery_status)) {
        throw new ApiError('service_unavailable');
      }
      if (result.capture_url) {
        setInviteUrl(result.capture_url);
        setInviteOutcome('A new invitation is ready. The revoked link still cannot be used.');
        setCopyFeedback('');
      } else {
        setInviteUrl('');
        setInviteOutcome('A new invitation email is queued. The revoked link still cannot be used.');
        setCopyFeedback('');
      }
      setInvitations((rows) => rows.map((row) => (
        row.invitation_id === invitation.invitation_id
          ? { ...row, can_invite_again: false }
          : row
      )));
      try {
        setInvitations(await fetchVoiceInvitations(selectedHouseholdId));
      } catch (refreshError) {
        setListError(toApiError(refreshError));
      }
      window.requestAnimationFrame?.(() => {
        document.getElementById('invite-title')?.scrollIntoView?.({
          behavior: 'smooth',
          block: 'start',
        });
      });
    } catch (requestError) {
      setListError(toApiError(requestError));
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (invitation: VoiceInvitationStatus) => {
    if (!window.confirm(`Revoke the invitation for ${invitation.recipient_email || 'this voice donor'}? The link will stop working.`)) return;
    setBusyId(invitation.invitation_id);
    try {
      await cancelVoiceInvitation(invitation.invitation_id);
      setInvitations((rows) => rows.map((row) => row.invitation_id === invitation.invitation_id ? {
        ...row,
        state: 'revoked',
        consent_status: 'revoked',
        can_resend: false,
        can_revoke: false,
        can_invite_again: false,
      } : row));
      if (selectedHouseholdId) {
        try {
          setInvitations(await fetchVoiceInvitations(selectedHouseholdId));
        } catch (refreshError) {
          setListError(toApiError(refreshError));
        }
      }
    } catch (requestError) {
      setListError(toApiError(requestError));
    } finally {
      setBusyId(null);
    }
  };

  const copyLink = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopyFeedback('Link copied.');
    } catch {
      setCopyFeedback('Could not copy. Select the link and copy it manually.');
    }
  };

  if (loading) return <div className="voice"><StateMessage kind="loading" title="Loading invitations…" shape="form" headingLevel={1} /></div>;
  if (error) return <div className="voice"><StateMessage kind="error" error={error} title="Invitations are unavailable" headingLevel={1} retry={{ onClick: () => setRetryNonce((nonce) => nonce + 1) }} /></div>;

  const busyFor = (invitation: VoiceInvitationStatus) => busyId === invitation.invitation_id;

  return (
    <div className="voice voice-invitations">
      <div className="voice__back">
        <PillButton variant="secondary" size="compact" to="/voice" icon={<ArrowLeftIcon size={20} />}>Family voices</PillButton>
      </div>
      <header className="voice__title">
        <h1>Voice invitations</h1>
        <p className="voice__subtitle">Invite someone to record a private Family voice and the personal moments for the listeners you choose. Consent stays with the voice donor.</p>
      </header>

      <Panel purpose="settings" labelledBy="invite-title" className="voice-panel">
        <h2 id="invite-title">Invite someone to record</h2>
        <div className="voice-form">
          {householdIds.length > 1 && (
            <FormField id="voice-invite-household" label="Family">
              {(control) => (
                <select {...control} value={selectedHouseholdId} onChange={(event) => changeHousehold(event.target.value)}>
                  {householdIds.map((id, index) => <option value={id} key={id}>Family {index + 1}</option>)}
                </select>
              )}
            </FormField>
          )}
          <FormField id="voice-donor-email" label="Email">
            <input type="email" value={inviteEmail} onChange={(event) => setInviteEmail(event.target.value)} placeholder="grandma@example.com" autoComplete="email" />
          </FormField>
          <fieldset className="voice-choice">
            <legend>Who should hear this voice?</legend>
            {householdListeners.length ? householdListeners.map((listener) => (
              <label key={listener.listener_id} className="voice-choice__option">
                <input type="checkbox" aria-label={listener.display_name} checked={inviteListenerIds.has(listener.listener_id)} onChange={(event) => setInviteListenerIds((current) => { const next = new Set(current); if (event.target.checked) next.add(listener.listener_id); else next.delete(listener.listener_id); return next; })} />
                <span>{listener.display_name}</span>
              </label>
            )) : <p className="voice-choice__empty">Add a listener in Account &amp; family first.</p>}
          </fieldset>
          <div className="voice-panel__actions">
            <PillButton variant="primary" disabled={creatingInvite || !inviteEmail.trim() || !inviteListenerIds.size} busy={creatingInvite} busyLabel="Preparing invitation…" onClick={() => void handleInvite()}>Create private invitation</PillButton>
          </div>
          {inviteError && <StateMessage kind="error" urgent error={inviteError} title="The invitation was not created" retry={{ onClick: () => void handleInvite() }} />}
          {inviteOutcome && (
            <div className="voice-invite-result" role="status">
              <p>{inviteOutcome}</p>
              {inviteUrl && (
                <>
                  <FormField id="voice-invite-link" label="Invitation link">
                    <input readOnly value={inviteUrl} onFocus={(event) => event.currentTarget.select()} />
                  </FormField>
                  <div className="voice-panel__actions">
                    <PillButton variant="secondary" size="compact" onClick={() => void copyLink()}>Copy link</PillButton>
                    {copyFeedback && <span className="voice-invite-result__feedback">{copyFeedback}</span>}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </Panel>

      <Panel purpose="settings" labelledBy="invitation-list-title" className="voice-panel">
        <div className="voice-panel__head">
          <h2 id="invitation-list-title">Sent invitations</h2>
          <span className="voice-panel__count">{invitations.length}</span>
        </div>
        {listLoading && <StateMessage kind="loading" title="Checking invitations…" shape="text" />}
        {listError && <StateMessage kind="error" error={listError} title="Invitations could not be refreshed" retry={{ onClick: () => setListRetryNonce((nonce) => nonce + 1) }} />}
        {!listLoading && !listError && invitations.length === 0 && (
          <div className="voice-panel__empty">
            <p><strong>No invitations yet</strong></p>
            <p>When you invite a voice donor, their consent and progress will appear here.</p>
          </div>
        )}
        {!listLoading && !listError && invitations.length > 0 && (
          <ul className="invitation-list">
            {invitations.map((invitation) => (
              <li className="invitation-row" key={invitation.invitation_id}>
                <div className="invitation-row__main">
                  <strong>{invitation.donor_name || invitation.recipient_email || 'Invited storyteller'}</strong>
                  <span>For {invitation.listeners.map((listener) => listener.display_name).join(' and ')}{invitation.voice_name ? ` · ${invitation.voice_name}` : ''}</span>
                  <span>{invitation.state === 'revoked' ? 'This link no longer works' : invitation.requires_updated_consent_form ? 'Create an updated invitation before recording' : captureStageLabel(invitation.capture_stage)}</span>
                  <small>{invitation.voice_sample_count}/6 voice samples · {invitation.listener_completion_count}/{invitation.listeners.length} listener messages</small>
                  <small>{buildStatusLabel(invitation.build_status)}</small>
                </div>
                <div className="invitation-row__states">
                  <span className="invitation-row__consent" data-tone={invitationStatusTone(invitation)}>{invitationStatusLabel(invitation)}</span>
                  <small>{invitation.state === 'revoked' ? 'Revoked' : invitation.requires_updated_consent_form ? 'Previous consent form' : invitationStateLabel(invitation.state)} · {deliveryLabel(invitation.delivery_status)}</small>
                  <small>{formatExpiry(invitation.expires_at)}</small>
                  <div className="invitation-row__actions">
                    {invitation.can_resend && <PillButton variant="secondary" size="compact" disabled={busyFor(invitation)} onClick={() => void resend(invitation)}>{hasEmailDelivery(invitation.delivery_status) ? 'Send again' : 'New link'}</PillButton>}
                    {invitation.can_resend && hasEmailDelivery(invitation.delivery_status) && <PillButton variant="secondary" size="compact" disabled={busyFor(invitation)} onClick={() => void createManualLink(invitation)}>Recovery link</PillButton>}
                    {invitation.can_invite_again && <PillButton variant="secondary" size="compact" disabled={busyFor(invitation)} onClick={() => void inviteAgain(invitation)}>Invite again</PillButton>}
                    {invitation.can_create_updated_invitation && <PillButton variant="secondary" size="compact" disabled={busyFor(invitation)} onClick={() => void createUpdatedInvitation(invitation)}>Create updated invitation</PillButton>}
                    {invitation.can_revoke && <PillButton variant="destructive" size="compact" disabled={busyFor(invitation)} onClick={() => void revoke(invitation)}>Revoke</PillButton>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
};

export default VoiceInvitations;
